<script setup>
import {ref, watch, nextTick} from 'vue'
import {Link, router} from '@inertiajs/vue3'
import {useSearchStore} from '@js/store/SearchStore.js'
import IconX from '@js/components/Icons/IconX.vue'
import ImageLoader from '@js/components/Loaders/ImageLoader.vue'
import NumberFormatComponent from '@js/components/Formatter/NumberFormatComponent.vue'
import SearchDesktopLoadingSkeleton from '@js/components/Search/SearchDesktopLoadingSkeleton.vue'
import IconArrowRight from '@js/components/Icons/IconArrowRight.vue'
import IconSearch from '@js/components/Icons/IconSearch.vue'

const searchStore = useSearchStore()
const searchInput = ref(null)

// Подгрузка данных при изменении query
watch(() => searchStore.query, () => {
    searchStore.getSearchData()
})

// Сброс при навигации
router.on('before', () => {
    searchStore.show = false
    searchStore.query = ''
})

// Фокус в input при открытии
watch(() => searchStore.show, async shown => {
    if (shown) {
        await nextTick()
        searchInput.value?.focus()
    }
})
</script>

<template>
    <div v-if="searchStore.show" class="fixed inset-0 bg-white z-50 flex flex-col">
        <!-- Top bar with input and button -->
        <div class="flex items-center p-4 border-b border-gray-200">
            <button @click.prevent="searchStore.show = false" class="mr-3">
                <IconArrowRight class="w-5 h-5 text-gray-700 rotate-180"/>
            </button>

            <div class="relative w-full" ref="searchInputRef">
                <input
                    type="search"
                    id="location-search"
                    ref="searchInput"
                    v-model="searchStore.query"
                    @click.prevent="searchStore.showDropdown"
                    class="block p-2.5 w-full z-20 text-gray-900 rounded border-baseColor border-2 focus:ring-0 focus:border-baseColor"
                    :placeholder="searchStore.currentPlaceholder"
                />

                <button v-if="searchStore.query"
                        @click.prevent="searchStore.clearQuery"
                        type="button"
                        class="absolute inset-y-0 end-[50px] flex items-center px-2 text-gray-400"
                        aria-label="Очистить поиск">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2"
                         viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round"
                              d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>

                <Link
                    type="submit"
                    :href="route('searchPage', {search: searchStore.query})"
                    @click.prevent="searchStore.initSearch"
                    class="flex items-center justify-center absolute top-0 end-0 h-full p-2.5 text-sm font-medium text-black bg-baseColor rounded-e border border-baseColor md:hover:bg-baseColor/80 focus:outline-none focus:ring-0 transition"
                >
                    <IconSearch class="w-5 h-5 text-black"/>
                </Link>
            </div>
        </div>

        <!-- Search results -->
        <div class="flex-1 overflow-y-auto">
            <template v-if="searchStore.resultsLoaded">
                <div v-if="searchStore.results.length">
                    <Link v-for="result in searchStore.results" :key="result.id"
                          :href="route('product', result.slug)"
                          class="flex items-center gap-4 px-4 py-3 border-b md:hover:bg-gray-50 cursor-pointer transition">
                        <ImageLoader :src="result.image_url"
                                     :title="result.title"
                                     imgClasses="w-[70px] h-[70px] object-cover rounded-md border"/>
                        <div>
                            <div v-if="result.article" class="text-xs text-gray-500 font-medium">
                                Артикул: {{ result.article }}
                            </div>
                            <div class="text-sm font-medium">
                                {{ result.title }}
                            </div>
                            <div class="text-xs text-gray-500" v-if="result.category">
                                {{ result.category }}
                            </div>

                            <!-- НОВЫЙ ПРОЕКТ: цена только одна (price = ₽/тонну) -->
                            <div class="text-sm font-semibold text-yellow-600">
                                <NumberFormatComponent
                                    v-if="Number(result.price ?? 0) > 0"
                                    :number="result.price"
                                    :append_text="' ₽/' + (result.price_type === 'kg' ? 'кг' : 'тонна')"
                                />
                                <div v-else>
                                    Договорная
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>

                <div v-else class="px-4 py-3 text-sm text-gray-500">
                    {{ searchStore.query ? 'Ничего не найдено' : 'Введите для поиска' }}
                </div>
            </template>

            <template v-else>
                <SearchDesktopLoadingSkeleton v-for="i in 4" :key="i"/>
            </template>

            <!-- История -->
            <div v-if="searchStore.history.length" class="p-3 border-t text-sm text-gray-400">
                История
            </div>
            <ul v-if="searchStore.history.length" class="mb-2">
                <li v-for="item in searchStore.history" :key="item"
                    class="px-4 py-2 text-sm text-gray-700 md:hover:bg-grayBg flex justify-between items-center">
                    <span @click.prevent="searchStore.repeatHistory(item)" class="cursor-pointer w-full">
                        {{ item }}
                    </span>
                    <button @click.stop.prevent="searchStore.removeFromHistory(item)"
                            class="group ml-2 text-gray-400 transition text-xs">
                        <IconX class="w-4 h-4 group-hover:stroke-red-600 transition"/>
                    </button>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
